/**
 * Status, filter, and display config for field events.
 */
import {
  Check,
  CheckCircle2,
  Circle,
  CircleDashed,
  Clock4,
  GraduationCap,
  Heart,
  ShieldCheck,
  User,
  UserPlus,
  UserX,
  XCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { getRoleLabel, getRoleLabelPlural } from '@/utils/role-label-utils';
import type {
  EventAttendanceValue,
  EventCancelReason,
  EventStatus,
  RecurrenceType,
} from '@/types/event.types';
import type { BadgeVariant } from '@/components/common/LabelBadge';

/** Badge + icon config for an event status. */
export interface EventStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor: string;
  /** Solid fill when the status filter is active (IN_PROGRESS). */
  filterActiveVariant?: 'solid';
  /** Blinking red dot for the in-progress status. */
  isInProgress?: boolean;
}

/** Badge config for each event status. */
export const EVENT_STATUS_CONFIG: Record<EventStatus, EventStatusConfig> = {
  PLANNED: {
    label: 'Planifié',
    icon: Clock4,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-1)',
  },
  IN_PROGRESS: {
    label: 'En cours',
    icon: Circle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
    filterActiveVariant: 'solid',
    isInProgress: true,
  },
  COMPLETED: {
    label: 'Terminé',
    icon: CheckCircle2,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-success))',
  },
  CLOSED: {
    label: 'Clôturé',
    icon: CheckCircle2,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--accent-purple))',
  },
  CANCELLED: {
    label: 'Annulé',
    icon: XCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
};

/** Recurrence type labels. */
export const EVENT_RECURRENCE_LABELS: Record<string, string> = {
  daily: 'Quotidienne',
  weekly: 'Hebdomadaire',
  several_days_per_week: 'Plusieurs jours / semaine',
  biweekly: 'Bi-hebdomadaire',
  monthly: 'Mensuelle',
  custom: 'Personnalisée',
  ab: 'Bi-mensuelle (A/B)',
};

/** Filter group IDs for the events {@link DataTable} configure panel. */
export type FilterGroupId =
  | 'filter-search'
  | 'filter-period'
  | 'filter-date-range'
  | 'filter-status'
  | 'filter-establishment'
  | 'filter-level'
  | 'filter-recurrence'
  | 'filter-activity-type'
  | 'filter-activity';

/** Configure-panel order for event filter groups. */
export const ALL_FILTER_GROUP_IDS: FilterGroupId[] = [
  'filter-search',
  'filter-period',
  'filter-date-range',
  'filter-status',
  'filter-establishment',
  'filter-level',
  'filter-recurrence',
  'filter-activity-type',
  'filter-activity',
];

/** Human-readable label per filter group (configure panel). */
export const FILTER_GROUP_LABELS: Record<FilterGroupId, string> = {
  'filter-search': 'Recherche participant',
  'filter-period': 'Période',
  'filter-date-range': 'Date personnalisée',
  'filter-status': 'Statut',
  'filter-establishment': 'Établissement',
  'filter-level': 'Niveau',
  'filter-recurrence': 'Récurrence',
  'filter-activity-type': "Type d'activité",
  'filter-activity': 'Activité',
};

/** Event list filter state. */
export interface EventFilters {
  search?: string;
  period?: 'all' | 'today' | 'week' | 'month' | 'custom';
  dateFrom?: string;
  dateTo?: string;
  status?: EventStatus[];
  establishmentIds?: string[];
  levelIds?: string[];
  recurrenceType?: RecurrenceType;
  activityTypeIds?: string[];
  activityIds?: string[];
}

/** Default event list filters. */
export const DEFAULT_EVENT_FILTERS: EventFilters = {
  search: undefined,
  period: 'month',
  dateFrom: undefined,
  dateTo: undefined,
  status: undefined,
  establishmentIds: undefined,
  levelIds: undefined,
  recurrenceType: undefined,
  activityTypeIds: undefined,
  activityIds: undefined,
};

/** Roles considered as event organizers. */
export const EVENT_ORGANIZER_ROLES = ['tutor', 'volunteer', 'coordinator'] as const;

/** Locked session format for field events. */
export const EVENT_FORMAT_LOCKED = 'in_person' as const;

/** Format options (in-person only). */
export const EVENT_FORMAT_OPTIONS = [{ value: EVENT_FORMAT_LOCKED, label: 'Présentiel' }] as const;

/** Page size for the participants column. */
export const EVENT_PARTICIPANTS_COLUMN_PAGE_SIZE = 5;

/** Organizer role group type when picking event organizers. */
export type EventOrganizerGroupType = 'tutor' | 'volunteer' | 'coordinator';

/** Organizer role group shown when picking event organizers. */
export interface EventOrganizerGroupOption {
  type: EventOrganizerGroupType;
  label: string;
  description: string;
  Icon: LucideIcon;
  accentColor: string;
}

/** Organizer role groups shown when picking event organizers. */
export const EVENT_ORGANIZER_GROUP_OPTIONS: EventOrganizerGroupOption[] = [
  {
    type: 'tutor',
    get label() {
      return getRoleLabelPlural('tutor');
    },
    description: 'Bénévoles encadrant les événements scolaires',
    Icon: GraduationCap,
    accentColor: 'var(--color-2-nuance-1)',
  },
  {
    type: 'volunteer',
    get label() {
      return `${getRoleLabelPlural('volunteer')} / ${getRoleLabelPlural('professor')}`;
    },
    description: 'Volontaires ou professeurs encadrant les événements scolaires',
    Icon: Heart,
    accentColor: 'var(--color-3-nuance-1)',
  },
  {
    type: 'coordinator',
    get label() {
      return getRoleLabelPlural('coordinator');
    },
    description: 'Coordinateurs supervisant les activités',
    Icon: ShieldCheck,
    accentColor: 'var(--color-4-nuance-1)',
  },
];

/** Student creation mode when adding an event participant. */
export type EventStudentCreationType = 'registered' | 'unregistered';

/** Student creation mode when adding an event participant. */
export interface EventStudentCreationOption {
  type: EventStudentCreationType;
  label: string;
  description: string;
  Icon: LucideIcon;
  accentColor: string;
}

/** Student creation modes when adding a participant. */
export const EVENT_STUDENT_CREATION_OPTIONS: EventStudentCreationOption[] = [
  {
    type: 'registered',
    get label() {
      return `${getRoleLabel('student')} inscrit`;
    },
    description: "Créer un compte élève dans l'application",
    Icon: GraduationCap,
    accentColor: 'var(--color-3-nuance-1)',
  },
  {
    type: 'unregistered',
    get label() {
      return `${getRoleLabel('student')} non inscrit`;
    },
    description: 'Ajouter un élève sans compte',
    Icon: UserPlus,
    accentColor: 'var(--color-2-nuance-1)',
  },
];

/** Cancellation reason options. */
export const EVENT_CANCEL_REASON_OPTIONS = [
  { value: 'insufficient_participants', label: 'Nombre de participants insuffisant' },
  { value: 'organizer_unavailable', label: 'Organisateur indisponible' },
  { value: 'room_unavailable', label: 'Salle indisponible' },
  { value: 'technical_issue', label: 'Problème technique' },
  { value: 'other', label: 'Autre raison' },
] as const satisfies ReadonlyArray<{ value: EventCancelReason; label: string }>;

/** Presence value for the event attendance control. */
export interface EventAttendanceOption {
  value: EventAttendanceValue;
  label: string;
  Icon: LucideIcon;
  color: string;
}

/** Attendance values for the event presence control. */
export const EVENT_ATTENDANCE_OPTIONS: EventAttendanceOption[] = [
  {
    value: 'present',
    label: 'Présent',
    Icon: Check,
    color: 'rgb(var(--color-success))',
  },
  {
    value: 'pending',
    label: 'ND',
    Icon: CircleDashed,
    color: 'var(--color-1-nuance-2)',
  },
  {
    value: 'absent',
    label: 'Absent',
    Icon: XCircle,
    color: 'rgb(var(--color-error))',
  },
];

/** Attendance form section id (organizers / students / unregistered). */
export type EventAttendanceSectionId = 'organizers' | 'students' | 'unregistered';

/** Attendance form section (organizers / students / unregistered). */
export interface EventAttendanceSection {
  id: EventAttendanceSectionId;
  title: string;
  Icon: LucideIcon;
  accentColor: string;
}

/** Attendance sections on the event presence form. */
export const EVENT_ATTENDANCE_SECTIONS: EventAttendanceSection[] = [
  {
    id: 'organizers',
    title: 'Organisateurs',
    Icon: GraduationCap,
    accentColor: 'var(--color-2-nuance-1)',
  },
  {
    id: 'students',
    title: 'Élèves inscrits',
    Icon: User,
    accentColor: 'var(--color-1-nuance-1)',
  },
  {
    id: 'unregistered',
    title: 'Participants non inscrits',
    Icon: UserX,
    accentColor: 'var(--color-3-nuance-1)',
  },
];

/** Fixed width of the attendance control. */
export const EVENT_ATTENDANCE_CONTROL_WIDTH = 270;

/** Attendance status display config: CSS class names and labels. */
export const EVENT_ATTENDANCE_STATUS_CONFIG: Record<string, { className: string; label: string }> =
  {
    present: { className: 'bg-green-100 text-green-800', label: 'Présent' },
    absent: { className: 'bg-red-100 text-red-800', label: 'Absent' },
    _default: { className: 'bg-gray-100 text-gray-800', label: 'En attente' },
  };

export { DATE_RANGE_PERIOD_OPTIONS_WITH_CUSTOM } from '@/constants/shared-catalog';

/** Tab id for the events list. */
export const EVENT_TAB_ID = 'tab-events';

/** Interface-element name for events. */
export const EVENTS_MENU_ELEMENT_ID = 'events';
